import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoTexto } from '@/componentes/Campo';
import { explicarFalha, pedir } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { primeiraMaiuscula } from '@/lib/texto';
import { COMPRIMENTO_MAXIMO_DA_SENHA } from '@/lib/senha';
import type { ContaDeSuporte } from '@/modulos/sessao/conta-de-suporte';
import { CHAVE_DA_SESSAO } from '@/modulos/sessao/consultas';

/**
 * A CONTA DE SUPORTE (0134, decisão 62), no perfil de quem também administra
 * a plataforma com o mesmo e-mail.
 *
 * Vincular pede a senha DA PLATAFORMA: é a prova de que a mesma pessoa é dona
 * das duas contas. Vinculada, a senha da plataforma passa a ser esta, entrar
 * aqui abre as duas, e o papel aparece como "Suporte". Se a senha desta
 * empresa for trocada por convite, as duas divergem, e o bloco pede a prova
 * de novo.
 */
export function VinculoDaConta({ conta, email }: { readonly conta: ContaDeSuporte; readonly email: string | null }) {
  const cliente = useQueryClient();
  const [senha, setSenha] = useState('');
  const [problema, setProblema] = useState<string | null>(null);

  const vincular = useMutation({
    mutationFn: () =>
      pedir<{ vinculada: true }>('/sessao/conta-de-suporte', { method: 'POST', body: JSON.stringify({ senhaDaPlataforma: senha }) }),
    meta: {
      retorno: retornoDe(() => ({
        titulo: 'Conta vinculada',
        efeito: 'A senha da administração da plataforma passou a ser a desta empresa',
      })),
    },
    onSuccess: async () => {
      setSenha('');
      await cliente.invalidateQueries({ queryKey: CHAVE_DA_SESSAO });
    },
  });

  const desvincular = useMutation({
    mutationFn: () => pedir<{ vinculada: false }>('/sessao/conta-de-suporte', { method: 'DELETE' }),
    meta: { retorno: retornoDe(() => ({ titulo: 'Vínculo desfeito', efeito: 'As duas contas voltam a ser separadas' })) },
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: CHAVE_DA_SESSAO });
    },
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (senha === '') {
      setProblema('Informe a senha da administração da plataforma.');
      return;
    }
    setProblema(null);
    vincular.mutate();
  }

  const recusa = vincular.isError ? explicarFalha(vincular.error) : desvincular.isError ? explicarFalha(desvincular.error) : null;

  if (conta.vinculada && conta.unificada) {
    return (
      <div className="flex flex-col gap-4">
        <p className="txt-corpo max-w-[72ch] text-pretty text-tinta">
          Esta conta está vinculada à administração da plataforma, e a senha é uma só para os dois ambientes. Entrar aqui
          abre também a administração, e trocar a senha aqui troca a de lá junto. O seu papel continua com tudo o que
          concede, e aparece como Suporte para a empresa.
        </p>
        {recusa !== null ? <Recusa erro={recusa.erro} comoResolver={recusa.comoResolver} /> : null}
        <div className="flex flex-wrap gap-3">
          <Link to="/plataforma" className="botao botao-secundario">
            Ir para a administração da plataforma
          </Link>
          <Botao
            nivel="terciario"
            disabled={desvincular.isPending}
            onClick={() => {
              desvincular.mutate();
            }}
          >
            {desvincular.isPending ? 'Desfazendo…' : 'Desfazer o vínculo'}
          </Botao>
        </div>
      </div>
    );
  }

  return (
    <form className="formulario-grade" onSubmit={enviar}>
      <p className="campo-linha txt-corpo max-w-[72ch] text-pretty text-tinta">
        {conta.vinculada
          ? 'As senhas dos dois ambientes ficaram diferentes: a desta empresa foi trocada por convite. Digite a senha da administração da plataforma para unificar de novo; a de lá passa a ser a desta empresa.'
          : 'Você também administra a plataforma com este e-mail. Digite a senha da administração uma vez para vincular as duas contas: a senha de lá passa a ser a desta empresa, entrar aqui abre as duas, e o seu papel aparece como Suporte.'}
      </p>
      {email ? <input type="email" autoComplete="username" value={email} readOnly hidden /> : null}
      <div className="campo-linha">
        <CampoTexto
          rotulo="Senha da administração da plataforma"
          valor={senha}
          tipo="senha"
          autoComplete="off"
          maxLength={COMPRIMENTO_MAXIMO_DA_SENHA}
          aoMudar={setSenha}
        />
      </div>
      {problema !== null ? (
        <p role="alert" className="campo-linha txt-apoio text-tinta">
          {problema}
        </p>
      ) : null}
      {recusa !== null ? (
        <div className="campo-linha">
          <Recusa erro={recusa.erro} comoResolver={recusa.comoResolver} />
        </div>
      ) : null}
      <div className="campo-linha barra-de-acoes">
        <Botao nivel="secundario" type="submit" disabled={vincular.isPending}>
          {vincular.isPending ? 'Vinculando…' : conta.vinculada ? 'Unificar a senha' : 'Vincular e unificar a senha'}
        </Botao>
      </div>
    </form>
  );
}

function Recusa({ erro, comoResolver }: { readonly erro: string; readonly comoResolver: string }) {
  return (
    <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
      <span className="txt-corpo text-tinta">{primeiraMaiuscula(erro)}</span>
      <span className="txt-apoio text-tinta-secundaria">{comoResolver}</span>
    </div>
  );
}
